import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import { css } from "styled-components"
import { HeroOverlay } from "../../../components/applications/hero.style"
import Projects from "../../../components/applications/projects"
import Testimonials from "../../../components/applications/testimonials"
import ClientSuccess from "../../../components/developer-tools/client-success"
import PrebuiltApps from "../../../components/developer-tools/prebuilt-apps"
import Contact from "../../../components/shared/contact"
import Footer from "../../../components/shared/footer"
import Hero from "../../../components/shared/hero"
import Layout from "../../../components/shared/layout"
import SEO from "../../../components/shared/seo"
import { IBreakpointImageVariants } from "../../../design-system/atoms/breakpoint-image"
import {
  IFluidImageQuery,
  IFluidImageQueryArray,
} from "../../../design-system/types/imageQuery.types"

interface IApplicationsPageStaticQuery {
  heroDesktop: IFluidImageQuery
  heroTablet: IFluidImageQuery
  heroMobile: IFluidImageQuery
  desktopLogos: IFluidImageQuery
  tabletLogos: IFluidImageQuery
  mobileLogos: IFluidImageQueryArray
}

interface IApplicationsPage {
  location: any
}

const Applications = ({ location }: IApplicationsPage) => {
  const {
    heroDesktop,
    heroTablet,
    heroMobile,
    desktopLogos,
    tabletLogos,
    mobileLogos,
  } = useStaticQuery<IApplicationsPageStaticQuery>(graphql`
    query {
      heroDesktop: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Hero Images/hero 1920.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      heroTablet: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Hero Images/hero 768.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 768) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      heroMobile: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Hero Images/hero 360.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 360) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      desktopLogos: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Clients/client logos desktop.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 460) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      tabletLogos: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Clients/client logos tablet.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1024) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mobileLogos: allFile(
        filter: {
          relativeDirectory: {
            eq: "1_SOLUTIONS/Pre-Built Applications/Clients/mobile"
          }
        }
        sort: { fields: base }
      ) {
        edges {
          node {
            id
            base
            childImageSharp {
              fluid(maxWidth: 600) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
    }
  `)

  const heroImages: IBreakpointImageVariants = {
    lg: heroDesktop.childImageSharp.fluid,
    md: heroTablet.childImageSharp.fluid,
    sm: heroMobile.childImageSharp.fluid,
  }
  return (
    <Layout location={location}>
      <SEO
        title="Wayfinding Applications"
        description="Mappedin applications provide best-in-class wayfinding. Our solutions for directory, web, and mobile platforms allow users to seamlessly navigate the indoors, while the Mappedin CMS enables easy map management across property portfolios."
      />
      <Hero
        sizes={heroImages}
        overlayStyle={HeroOverlay}
        title="Pre-Built Mapping Applications"
        description="Mappedin creates a digital twin of your property for all indoor mapping touchpoints. Used in hundreds of venues around the world, our pre-built applications have been developed with years worth of industry knowledge and are built on top of our robust platform."
      />
      <Projects />
      <ClientSuccess
        description="As we continually improve our pre-built applications, we value customer input. This dedication to customer success ensures your voice is heard when evolving our products."
        supportItems={[
          "24/7 Support",
          "Interactive Town Halls",
          "Continuous Training",
          "Expert Integrations Team",
          "Developer Documentation",
        ]}
        desktopLogos={desktopLogos}
        tabletLogos={tabletLogos}
        mobileLogos={mobileLogos}
        clientGridVariants={[
          null,
          css`
            grid-column: 1/-1;
            max-width: 720px;
          `,
          css`
            max-width: 580px;
          `,
        ]}
        showDivider
      />
      <Testimonials />
      <PrebuiltApps
        description="Check out our developer tools to discover other products that Mappedin offers."
        buttonText="Explore Developer Tools"
        buttonHref="/mapping/developer-tools/"
      />
      <Contact />
      <Footer />
    </Layout>
  )
}

export default Applications
